import { useMemo, useState } from "react";
import { DEFAULT_SHRINKAGE, artistScore } from "@trackmark/core";
import styles from "./Settings.module.css";

/**
 * Settings (design/README.md §12) — one scrolling column of plain sections.
 *
 * Two of the four sections carry rulings.
 *
 * **The tag queue is §J7.** A picker per row over 206 tags is 206 decisions, which means zero
 * decisions. So: sorted by occurrence descending, multi-select, bulk map and bulk ignore, and a
 * coverage line that makes the finish line real. Bulk ignore is not a tidying convenience — 45 of
 * the measured tags occur exactly once, and there is no world in which anyone files those.
 *
 * **The `M` slider is §J9.** It is a display gate, so moving it is non-destructive and safe to
 * expose — and the consequence table beneath it is what makes that legible: four real artists,
 * their raw mean and their adjusted score, recomputed as the slider moves. A slider with no
 * consequence attached is a number nobody can have an opinion about.
 */

export interface GenreRow {
  id: number;
  name: string;
  songCount: number;
}

export interface TagRow {
  tag: string;
  songCount: number;
  genreId: number | null;
  genre: string | null;
  ignored: boolean;
}

export interface ConsequenceArtist {
  id: number;
  name: string;
  ratedCount: number;
  raw: number | null;
}

/** What `/api/artwork/progress` says, or null until it has said anything. */
export interface ArtworkState {
  total: number;
  stored: number;
  unavailable: number;
  pending: number;
  etaSeconds: number | null;
  running: boolean;
  configured: boolean;
  throttled: boolean;
}

export interface SettingsProps {
  genres: GenreRow[];
  onAddGenre: (name: string) => void;
  onRemoveGenre: (id: number) => void;

  tags: TagRow[];
  occurrences: { decided: number; total: number; waiting: number };
  onBulk: (tags: string[], genreId: number | null, ignore: boolean) => void;

  shrinkageM: number;
  onShrinkageM: (value: number) => void;
  /** Four real artists, so the consequence table is about this library and not an example. */
  consequence: ConsequenceArtist[];
  collection: { totalRated: number; mean: number | null };

  /** Null until the first read; the section is absent rather than a row of noughts. */
  artwork: ArtworkState | null;
  onFetchArt: () => void;
  onStopArt: () => void;
  onRetryArt: () => void;

  lastExport: string | null;
  onExportCsv: () => void;
  onExportJson: () => void;
  onChangePassword: () => void;
}

const M_MIN = 5;
const M_MAX = 55;

/** `~18 MIN LEFT`, in the app's own voice (docs/IMPORT-PROPOSAL.md §5.2). */
function eta(seconds: number | null): string {
  if (seconds === null) return "";
  const minutes = Math.max(1, Math.round(seconds / 60));
  // Past two hours the minute is noise, and "~137 MIN LEFT" reads as a measurement rather than
  // the estimate it is.
  if (minutes >= 120) return ` · ~${Math.round(minutes / 60)} HRS LEFT`;
  return ` · ~${minutes} MIN LEFT`;
}

export function Settings({
  genres,
  onAddGenre,
  onRemoveGenre,
  tags,
  occurrences,
  onBulk,
  shrinkageM,
  onShrinkageM,
  consequence,
  collection,
  artwork,
  onFetchArt,
  onStopArt,
  onRetryArt,
  lastExport,
  onExportCsv,
  onExportJson,
  onChangePassword,
}: SettingsProps) {
  const [newGenre, setNewGenre] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [mapTo, setMapTo] = useState<number | null>(null);

  const toggle = (tag: string) =>
    setSelected((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const coverage = occurrences.total === 0 ? 0 : occurrences.decided / occurrences.total;

  /**
   * The consequence table, recomputed live from `M`.
   *
   * `artistScore` is the same function the rankings use, so what this table promises is what the
   * rankings will show. A separate formula here — even a correct one — would be a second
   * implementation of the only number on the screen anyone is judging.
   */
  const rows = useMemo(
    () =>
      consequence.map((artist) => ({
        artist,
        score: artistScore(
          artist.raw === null ? [] : Array.from({ length: artist.ratedCount }, () => artist.raw!),
          collection,
          { ...DEFAULT_SHRINKAGE, m: shrinkageM },
        ),
      })),
    [consequence, collection, shrinkageM],
  );

  return (
    <div className={styles.page}>
      <div className={styles.title}>Settings</div>

      {/* ------------------------------------------------ genre vocabulary */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Genre vocabulary</h2>
        <div className={styles.chips} data-testid="genre-chips">
          {genres.map((g) => (
            <span key={g.id} className={styles.chip} data-testid="genre-chip">
              {g.name}
              <span className={styles.chipCount}>{g.songCount.toLocaleString()}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${g.name}`}
                onClick={() => onRemoveGenre(g.id)}
              >
                ×
              </button>
            </span>
          ))}

          <form
            className={styles.addGenre}
            onSubmit={(event) => {
              event.preventDefault();
              if (newGenre.trim() === "") return;
              onAddGenre(newGenre.trim());
              setNewGenre("");
            }}
          >
            <input
              className={styles.addGenreInput}
              value={newGenre}
              aria-label="Add genre"
              placeholder="+ add genre"
              data-testid="add-genre"
              onChange={(event) => setNewGenre(event.target.value)}
            />
          </form>
        </div>
        <div className={styles.hint}>
          REMOVING A GENRE UNFILES ITS SONGS AND RETURNS ITS TAGS TO THE QUEUE. NO RATING IS TOUCHED.
        </div>
      </section>

      {/* ------------------------------------------------ the tag queue (§J7) */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Unmapped tags</h2>
          <span className={styles.badge} data-testid="tags-waiting">
            {tags.filter((t) => t.genreId === null && !t.ignored).length} WAITING
          </span>
        </div>

        {/* The coverage line: what makes the finish line real. Counted over occurrences, because
            the top twelve tags are 37% of them and 6% of the list. */}
        <div className={styles.coverage} data-testid="tag-coverage">
          <div className={styles.coverageTrack}>
            <div className={styles.coverageFill} style={{ width: `${coverage * 100}%` }} />
          </div>
          <span>
            {occurrences.decided.toLocaleString()} OF {occurrences.total.toLocaleString()} TAGGED SONGS DECIDED ·{" "}
            {Math.round(coverage * 100)}%
          </span>
        </div>

        {tags.length === 0 ? (
          <div className={styles.hint} data-testid="tags-empty">
            EVERY TAG HAS BEEN DECIDED.
          </div>
        ) : (
          <>
            <div className={styles.bulk} data-testid="tag-bulk">
              <span className={styles.bulkCount}>{selected.length} SELECTED</span>
              <select
                className={styles.select}
                aria-label="Map to"
                value={mapTo ?? ""}
                onChange={(event) => setMapTo(event.target.value === "" ? null : Number(event.target.value))}
              >
                <option value="">MAP TO…</option>
                {genres.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles.bulkAction}
                disabled={selected.length === 0 || mapTo === null}
                data-testid="bulk-map"
                onClick={() => {
                  onBulk(selected, mapTo, false);
                  setSelected([]);
                }}
              >
                {/* The count appears only once there is one: "Map 0 →" reads as a broken
                    template rather than a disabled control. */}
                {selected.length === 0 ? "Map →" : `Map ${selected.length} →`}
              </button>
              <button
                type="button"
                className={styles.bulkGhost}
                disabled={selected.length === 0}
                data-testid="bulk-ignore"
                // Permanent, and the label says so: this is the control that makes 45 once-seen
                // tags finishable rather than eternal.
                onClick={() => {
                  onBulk(selected, null, true);
                  setSelected([]);
                }}
              >
                {selected.length === 0 ? "Ignore permanently" : `Ignore ${selected.length} permanently`}
              </button>
            </div>

            <div className={styles.tagList}>
              {tags.map((t) => (
                <label key={t.tag} className={styles.tagRow} data-testid="tag-row" data-selected={selected.includes(t.tag) || undefined}>
                  <input
                    type="checkbox"
                    className={styles.check}
                    checked={selected.includes(t.tag)}
                    onChange={() => toggle(t.tag)}
                  />
                  <span className={styles.tagName}>{t.tag}</span>
                  <span className={styles.tagCount}>{t.songCount.toLocaleString()} SONGS</span>
                  <span className={styles.tagState} data-unmapped={t.genre === null && !t.ignored || undefined}>
                    {t.ignored ? "IGNORED" : (t.genre ?? "MAP TO ▾")}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ------------------------------------------------ album artwork
       *
       * A background JOB with a progress line, not a modal and not a blocking step. 1,323 albums
       * at MusicBrainz's one request a second is 22–44 minutes (docs/IMPORT-PROPOSAL.md §5.2),
       * the library is fully rateable throughout, and leaving this screen does not stop it.
       *
       * It lives here rather than on Import because it is not part of importing: it is a backlog
       * with a finish line, which is the same shape as the tag queue above it — hence the same
       * coverage bar and the same WAITING badge.
       */}
      {artwork !== null && artwork.total > 0 && (
        <section className={styles.section} data-testid="artwork">
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Album artwork</h2>
            {artwork.pending > 0 && (
              <span className={styles.badge} data-testid="artwork-waiting">
                {artwork.pending.toLocaleString()} WAITING
              </span>
            )}
          </div>

          <div className={styles.coverage} data-testid="artwork-progress">
            <div className={styles.coverageTrack}>
              <div
                className={styles.coverageFill}
                style={{ width: `${((artwork.total - artwork.pending) / artwork.total) * 100}%` }}
              />
            </div>
            <span>
              {artwork.running
                ? // The line the design asked for, verbatim: what it has settled, out of what
                  // there is, and how long the rest will take.
                  `ARTWORK · ${(artwork.total - artwork.pending).toLocaleString()} / ${artwork.total.toLocaleString()}${eta(artwork.etaSeconds)}`
                : `${artwork.stored.toLocaleString()} OF ${artwork.total.toLocaleString()} ALBUMS HAVE COVERS` +
                  // Stated separately because it is not backlog: an album the Archive does not
                  // hold is answered, and the typographic fallback is the answer.
                  (artwork.unavailable > 0 ? ` · ${artwork.unavailable.toLocaleString()} NONE ARCHIVED` : "")}
            </span>
          </div>

          <div className={styles.actions}>
            {artwork.running ? (
              <button type="button" className={styles.primaryAction} data-testid="artwork-stop" onClick={onStopArt}>
                Stop
              </button>
            ) : (
              <>
                {artwork.pending > 0 && (
                  <button
                    type="button"
                    className={styles.primaryAction}
                    data-testid="artwork-start"
                    disabled={!artwork.configured}
                    onClick={onFetchArt}
                  >
                    {`Fetch ${artwork.pending.toLocaleString()} covers`}
                  </button>
                )}
                {/* The secondary action, and secondary on purpose: `unavailable` is an ANSWER,
                    and asking again is worth it only when the question has changed. */}
                {artwork.unavailable > 0 && (
                  <button type="button" className={styles.action} data-testid="artwork-retry" onClick={onRetryArt}>
                    {`Try ${artwork.unavailable.toLocaleString()} again`}
                  </button>
                )}
              </>
            )}
          </div>

          <div className={styles.hint} data-testid="artwork-hint">
            {!artwork.configured
              ? // Said before the button is pressed rather than as a 503 after it: a deploy with
                // no bucket cannot store a cover, and that is a configuration fact, not a failure.
                "NO STORAGE IS CONFIGURED, SO THERE IS NOWHERE TO PUT A COVER. SEE DOCS/RAILWAY.MD §3.4."
              : artwork.throttled
                ? // Otherwise this is a progress line that sits still for a minute with no
                  // explanation, which is indistinguishable from the job being broken.
                  "MUSICBRAINZ IS REFUSING REQUESTS AND THE QUEUE IS HOLDING OFF. IT PICKS UP AGAIN BY ITSELF — NOTHING IS LOST."
                : artwork.running
                ? "ONE REQUEST A SECOND, AS MUSICBRAINZ ASKS. STOPPING LETS THE CURRENT ALBUM FINISH. YOU CAN LEAVE THIS SCREEN — RATING IS UNAFFECTED."
                : artwork.pending === 0
                  ? artwork.unavailable > 0
                    ? // Not a failure to fix: the Archive does not hold every cover, and those
                      // records wear the typographic fallback by design. Worth asking again only
                      // when the lookup itself has changed — pulling a track list re-queues one
                      // album on its own, because a pull settles that record's identity.
                      "EVERY ALBUM HAS BEEN LOOKED UP. ASKING AGAIN IS WORTH IT AFTER A LOOKUP CHANGES, NOT OTHERWISE."
                    : "EVERY ALBUM HAS BEEN LOOKED UP."
                  : "ONE REQUEST A SECOND, AS MUSICBRAINZ ASKS. IT RUNS IN THE BACKGROUND AND SURVIVES A RESTART — RATING IS UNAFFECTED WHILE IT FILLS IN."}
          </div>
        </section>
      )}

      {/* ------------------------------------------------ M (§J9) */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Shrinkage constant M</h2>
        <div className={styles.slider}>
          <input
            type="range"
            min={M_MIN}
            max={M_MAX}
            value={shrinkageM}
            className={styles.range}
            aria-label="Shrinkage constant M"
            data-testid="m-slider"
            onChange={(event) => onShrinkageM(Number(event.target.value))}
          />
          <span className={styles.sliderValue} data-testid="m-value">
            {shrinkageM}
          </span>
        </div>
        <p className={styles.explain}>
          An artist with twelve rated songs is a smaller claim than one with a hundred and forty-six. M is how much
          evidence it takes before an artist&rsquo;s own average is trusted over the collection&rsquo;s. Higher pulls
          small samples toward the middle; lower lets them stand. It changes what is shown, never what you rated.
        </p>

        <div className={styles.consequence} data-testid="consequence">
          <div className={styles.consequenceHead}>
            <span>ARTIST</span>
            <span>RATED</span>
            <span>RAW</span>
            <span>ADJUSTED</span>
          </div>
          {rows.map(({ artist, score }) => (
            <div key={artist.id} className={styles.consequenceRow} data-testid="consequence-row">
              <span className={styles.consequenceName}>{artist.name}</span>
              <span className={styles.consequenceNum}>{artist.ratedCount}</span>
              <span className={styles.consequenceNum}>{artist.raw === null ? "—" : artist.raw.toFixed(2)}</span>
              <span className={styles.consequenceNum} data-adjusted="true">
                {score.qualified && score.score !== null ? score.score.toFixed(2) : "—"}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------ export & account */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Export &amp; account</h2>
        <div className={styles.actions}>
          <button type="button" className={styles.primaryAction} data-testid="export-csv" onClick={onExportCsv}>
            Export ratings · CSV
          </button>
          <button type="button" className={styles.action} data-testid="export-json" onClick={onExportJson}>
            Export everything · JSON
          </button>
          <button type="button" className={styles.action} onClick={onChangePassword}>
            Change password
          </button>
        </div>
        <div className={styles.hint} data-testid="last-export">
          {/* A quiet nudge, not a nag — and never a warning, because a library that has never been
              exported is a normal state on day one. */}
          {lastExport === null ? "NOTHING HAS BEEN EXPORTED YET." : `LAST EXPORT ${lastExport}`}
        </div>
        <div className={styles.hint}>ONE ACCOUNT. NO THIRD-PARTY SIGN-IN.</div>
      </section>
    </div>
  );
}
